import React, { useEffect, useState } from 'react';
import { useJarvisContext } from '../../context/JarvisContext';

// Mirrors EnableNotifications.jsx's pattern, but for the microphone.
// Previously NOTHING proactively asked for mic permission — the mic
// button (ChatInput.jsx/BottomBar.jsx) just called SpeechRecognition's
// .start() on tap, which triggers the browser's own permission prompt
// the very first time, but gives no explanation beforehand and — once
// denied even once, accidentally or not — the browser stops showing that
// prompt at all. From then on the mic silently does nothing forever,
// which is exactly what was reported. This surfaces the ask clearly, and
// critically, tells the DENIED case apart from the NOT-YET-ASKED case,
// since only the second one can be fixed by asking again — a denied
// permission needs the user to go change it in the browser's site
// settings themselves; no JS call can re-trigger that prompt.
const IS_ELECTRON = !!(window.electronAPI && window.electronAPI.isElectron);
const UA = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const IS_IOS = /iPad|iPhone|iPod/.test(UA) && !window.MSStream;

function deniedInstructions() {
  if (IS_IOS) return 'Settings app → Safari → Microphone मध्ये जाऊन या site साठी allow करा.';
  return 'पत्त्याच्या डाव्या बाजूला 🔒/ⓘ आयकॉनवर टॅप करा → Permissions → Microphone → Allow निवडा, मग page reload करा.';
}

export default function EnableMicrophone() {
  const { state } = useJarvisContext();
  const onChatScreen = state.activeScreen === 'v5chat';
  const drawerOpen = state.sidebarOpen;

  const [status, setStatus] = useState('unknown'); // 'unknown' | 'prompt' | 'granted' | 'denied' | 'unsupported'
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('jarvis_mic_dismissed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    if (IS_ELECTRON) return;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setStatus('unsupported');
      return;
    }
    let permStatus;
    const applyState = (s) => setStatus(s.state); // 'granted' | 'denied' | 'prompt'
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'microphone' })
        .then((s) => {
          permStatus = s;
          applyState(s);
          s.onchange = () => applyState(s);
        })
        .catch(() => setStatus('prompt')); // browser doesn't support querying 'microphone' — fall back to showing the ask
    } else {
      setStatus('prompt');
    }
    return () => { if (permStatus) permStatus.onchange = null; };
  }, []);

  if (
    IS_ELECTRON || dismissed || onChatScreen || drawerOpen ||
    status === 'granted' || status === 'unsupported' || status === 'unknown'
  ) return null;

  const isDenied = status === 'denied';

  const handleEnable = async () => {
    try {
      // Requesting just to trigger the permission prompt — don't need to
      // keep the stream open, SpeechRecognition opens its own later.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setStatus('granted');
    } catch {
      setStatus('denied'); // user tapped Block, or it was already denied
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('jarvis_mic_dismissed', '1'); } catch { /* ignore */ }
  };

  return (
    <div style={{
      // Stacked above InstallPWA (100px) and EnableNotifications (172px).
      position: 'fixed', left: '12px', right: '12px', bottom: '244px', zIndex: 1500,
      background: '#111827', border: `1px solid ${isDenied ? '#ef4444' : '#00d4ff'}`, borderRadius: '10px',
      padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px',
      fontFamily: 'monospace', boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
    }}>
      <span style={{ fontSize: '20px' }}>🎙️</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '700' }}>
          {isDenied ? 'Microphone Blocked' : 'Enable Microphone'}
        </div>
        <div style={{ color: '#94a3b8', fontSize: '11px' }}>
          {isDenied ? deniedInstructions() : 'बोलून JARVIS ला विचारण्यासाठी mic access लागतो'}
        </div>
      </div>
      {!isDenied && (
        <button onClick={handleEnable} style={{
          background: '#00d4ff', color: '#000', border: 'none', borderRadius: '6px',
          padding: '8px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
          fontFamily: 'monospace', flexShrink: 0,
        }}>Enable</button>
      )}
      <button onClick={handleDismiss} aria-label="Dismiss" style={{
        background: 'transparent', color: '#94a3b8', border: 'none', fontSize: '16px',
        cursor: 'pointer', padding: '4px', flexShrink: 0,
      }}>✕</button>
    </div>
  );
}
